import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { catchError, switchMap, take, throwError, timeout } from 'rxjs';
import { AuthStore } from '../store/auth/auth.store';
import { ReauthService, TOKEN_TIMEOUT_MS } from '../services/reauth.service';
import { withToken } from './http-header.interceptor';

/**
 * Recovers a request the API rejected with a 401 by refreshing the access
 * token and retrying once. When no new token can be issued, or the retry is
 * rejected too, the session cannot be recovered without a login, so the user
 * is sent through reauthentication. The 401 still reaches the caller so it can
 * keep the user's work in a retryable state.
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const authStore = inject(AuthStore);
  const reauthService = inject(ReauthService);

  const isSessionRejection = (response: unknown) =>
    response instanceof HttpErrorResponse &&
    response.status === 401 &&
    req.headers.has('Authorization');

  const reauthenticate = (response: unknown) => {
    reauthService.reauthenticate();
    return throwError(() => response);
  };

  return next(req).pipe(
    catchError((response: unknown) => {
      if (!isSessionRejection(response)) {
        return throwError(() => response);
      }
      return authService.getAccessTokenSilently({ cacheMode: 'off' }).pipe(
        timeout(TOKEN_TIMEOUT_MS),
        take(1),
        catchError(() => reauthenticate(response)),
        switchMap((token) => {
          authStore.setToken(token);
          return next(withToken(req, token)).pipe(
            catchError((retryResponse: unknown) =>
              isSessionRejection(retryResponse)
                ? reauthenticate(retryResponse)
                : throwError(() => retryResponse)
            )
          );
        })
      );
    })
  );
};
